import {memo, type ReactNode, useMemo, useState} from "react";
import {getPackagingLabel} from "../Cart/cartUtils";
import {Icon} from "../common/components/icon/Icon";
import {IconType} from "../common/components/icon/IconType";
import {type PackagingInformationEntry, type WarehouseStockData} from "../models/ProductData";
import {useIsMinDesktopSizeL, useIsMinTabletSize} from "../common/hooks/useMediaQuery";
import {DateFormatType, formatDate} from "../utils/DateFormatter";
import {PopperTooltip} from "../common/components/Tooltip";
import {IconClickable, IconClickableType} from "../common/components/icon/IconClickable";
import {useTranslation, type WithTranslation} from "react-i18next";
import {type ColumnDef} from "@tanstack/react-table";
import {TanStackTable} from "~/common/components/Table/TanStackTable";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

export interface RowData {
  index: number;
  products: ProductDescriptor[];
  showExpandableStock: boolean;
  isStockExpanded: boolean;
  showExpandableProduction: boolean;
  isProductionExpanded: boolean;
}

interface AvailabilityTableProps {
  products: ProductDescriptor[];
  placeholders: number[];
  colSize: number;
}

const getPackagingCode = (
  packageEntry: PackagingInformationEntry | undefined,
  product: ProductDescriptor,
  t: WithTranslation["t"],
) => {
  if (packageEntry?.packSize === undefined) {
    return "-";
  } else {
    return getPackagingLabel({
      t,
      productDescriptor: product,
      packSize: packageEntry?.packSize,
      packCode: packageEntry?.packCode,
    });
  }
};

const getStockInfo = (wh: WarehouseStockData | undefined, t: WithTranslation["t"]) => {
  const stockInfo: [string, string][] = [];
  if (!wh) {
    return stockInfo;
  }
  wh.inStock.forEach((stock) => {
    stockInfo.push(["" + stock.quantity, stock.date ? formatDate(stock.date, DateFormatType.DATE) : ""]);
  });
  wh.inProduction.forEach(({ date, quantity }) => {
    stockInfo.push(["" + quantity, formatDate(date, DateFormatType.DATE)]);
  });
  if (wh.inClarification > 0) {
    stockInfo.push(["" + wh.inClarification, t("quote.status.definition.inClarification")]);
  }
  return stockInfo;
};

const AvailabilityTable = memo(({ products, placeholders, colSize }: AvailabilityTableProps) => {
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  const isTablet = useIsMinTabletSize();
  const [isExpandedStockTable, setIsExpandedStockTable] = useState(false);
  const [isExpandedProductionTable, setIsExpandedProductionTable] = useState(false);

  const columnConfig: ColumnDef<RowData>[] = useMemo(() => {
    const columnConfig: ColumnDef<RowData>[] = [];

    const labelCol: ColumnDef<RowData> = {
      id: "label",
      header: t("availabilityTable.tableName") as string,
      size: 224,

      cell: (cell) => {
        const data = cell.row.original;
        switch (data.index) {
          case 0:
            return t("availabilityTable.packagingCode");
          case 1:
            return t("availabilityTable.shipping");
          case 2: {
            const hasMultipleStock = data.showExpandableStock;
            const isExpandedStockTable = data.isStockExpanded;
            return (
              <div className="scha__availability-table__expandable-column">
                <div className="column-name">{t("common.product.availability")}</div>
                {hasMultipleStock && (
                  <div className="expand" onClick={() => setIsExpandedStockTable(!isExpandedStockTable)}>
                    <Icon className="mr-1" type={isExpandedStockTable ? IconType.ChevronUp : IconType.ChevronDown} />
                    {isExpandedStockTable ? t("searchPage.refinements.showLess") : t("availabilityTable.showAll")}
                  </div>
                )}
              </div>
            );
          }
          case 3: {
            const hasMultipleProduction = data.showExpandableProduction;
            const isExpandedProductionTable = data.isProductionExpanded;
            return (
              <div className="scha__availability-table__expandable-column">
                <div className="column-name">{t("availabilityTable.plannedProduction")}</div>
                {hasMultipleProduction && (
                  <div className="expand" onClick={() => setIsExpandedProductionTable(!isExpandedProductionTable)}>
                    <Icon
                      className="mr-1"
                      type={isExpandedProductionTable ? IconType.ChevronUp : IconType.ChevronDown}
                    />
                    {isExpandedProductionTable ? t("searchPage.refinements.showLess") : t("availabilityTable.showAll")}
                  </div>
                )}
              </div>
            );
          }
        }
      },
    };

    const renderStockTable = (data: [ReactNode, ReactNode][], showExpanded: boolean) => {
      if (data.length > 3 && !showExpanded) {
        data = [...data.slice(0, 3), ["...", ""]];
      }
      return data.map((row, index) => (
        <div key={index} className={"d-flex"}>
          <div className={"d-flex"} style={{ width: "100px" }}>
            <span style={{ width: "50px", textAlign: "right" }}>{row[0] !== "[object Object]" && row[0]}</span>
          </div>
          <div className={"d-flex"}>{row[1]}</div>
        </div>
      ));
    };

    const makeProductCol = (index: number) => {
      const productCol: ColumnDef<RowData> = {
        id: `product-${index}`,
        header: "",
        size: colSize,
        cell: (cell) => {
          const data = cell.row.original;
          const product = data.products[index];
          if (!product) {
            return null;
          }
          const packageEntry = product.availability?.packagingInformation[0];
          const preferredPackQuantity = packageEntry?.preferredPackQuantity;

          switch (data.index) {
            case 0:
              return (
                <div className="d-flex">
                  <span className="mr-1">{packageEntry && getPackagingCode(packageEntry, product, t)}</span>
                  {preferredPackQuantity && preferredPackQuantity > 0 ? (
                    <PopperTooltip
                      zIndex={1300}
                      tooltipContent={
                        <div className="f-no-wrap">
                          {t("addToCartPopup.prefPackQuantity", { count: preferredPackQuantity })}
                        </div>
                      }
                    >
                      <IconClickable clickableType={IconClickableType.SECONDARY} type={IconType.InformationCircle} />
                    </PopperTooltip>
                  ) : null}
                </div>
              );
            case 1:
              return t(packageEntry?.warehouses[0]?.name as string);
            case 2: {
              const wh = packageEntry?.warehouses[0];
              const whData = getStockInfo(wh, t);
              return renderStockTable(whData, data.isStockExpanded);
            }
            case 3: {
              const global = packageEntry?.global;
              const globalData = getStockInfo(global, t);
              return renderStockTable(globalData, data.isProductionExpanded);
            }
          }
        },
      };
      return productCol;
    };

    isTablet && columnConfig.push(labelCol);
    products.forEach((product, index) => {
      columnConfig.push(makeProductCol(index));
    });
    placeholders.forEach((product) => {
      columnConfig.push(makeProductCol(product));
    });

    if (isDesktopL) {
      const len = columnConfig.length;
      columnConfig[len - 1].size = undefined;
    }

    return columnConfig;
  }, [colSize, isDesktopL]);

  const data = useMemo(
    () => createRowData(products, isExpandedStockTable, isExpandedProductionTable),
    [products, isExpandedStockTable, isExpandedProductionTable],
  );

  return (
    <TanStackTable
      data={data}
      columns={columnConfig}
      variant={"productComparison"}
      state={{ columnPinning: { left: ["label"] } }}
    />
  );
});

export default AvailabilityTable;

const createRowData = (
  products: ProductDescriptor[],
  isStockExpanded: boolean,
  isProductionExpanded: boolean,
): RowData[] => {
  const data: RowData[] = [];

  const packagesToCheck = products
    .filter((product) => product.availability && product.availability.packagingInformation.length > 0)
    .map((product) => product.availability!.packagingInformation[0]);

  const showExpandableStock = packagesToCheck.some((pack) => isTableDataBigEnough(pack.warehouses[0]));
  const showExpandableProduction = packagesToCheck.some((pack) => isTableDataBigEnough(pack.global));

  for (let i = 0; i < 4; i++) {
    data.push({
      index: i,
      products,
      isStockExpanded,
      isProductionExpanded,
      showExpandableProduction,
      showExpandableStock,
    });
  }

  return data;
};

const isTableDataBigEnough = (warehouse: WarehouseStockData) => {
  if (!warehouse) {
    return false;
  }
  const lineAmount = warehouse.inStock.length + warehouse.inProduction.length + (warehouse.inClarification > 0 ? 1 : 0);
  return lineAmount > 3;
};
